import { ReactNode } from "react";
import { FormControlLabel, FormControlLabelProps, Skeleton, Typography } from "@mui/material";
import styles from "./Label.module.css";

type LabelTextContentProps = {
	header?: ReactNode;
	children?: ReactNode;
};

export type CoreLabelTextSkeletonProps = { header?: boolean; body?: boolean };

export type CoreLabelTextProps = LabelTextContentProps & { htmlFor: string; id?: string };

export type CoreFormLabelProps = Omit<FormControlLabelProps, "disableTypography" | "label"> & LabelTextContentProps;

export function CoreLabelTextContent({ header, children }: LabelTextContentProps) {
	const headerElem = <span className={styles.labelHeader}>{header}</span>;

	if (header && children) {
		return (
			<div className={styles.stackedLabel}>
				{headerElem}
				{children}
			</div>
		);
	} else if (header) {
		return headerElem;
	} else {
		return typeof children === "string" ? (
			<Typography component={"span"} className={styles.singleLabel}>
				{children}
			</Typography>
		) : (
			children
		);
	}
}

export function CoreLabelText({ htmlFor, id, ...contentProps }: CoreLabelTextProps) {
	return (
		<label id={id} htmlFor={htmlFor}>
			<CoreLabelTextContent {...contentProps} />
		</label>
	);
}

export function CoreLabelTextSkeleton({ header = true, body = true }: CoreLabelTextSkeletonProps) {
	const headerElem = <Skeleton variant="text" width={100} />;
	const bodyElem = <Skeleton variant="text" width="100%" />;

	if (header && body) {
		return (
			<div className={styles.stackedLabel}>
				{headerElem}
				{bodyElem}
			</div>
		);
	} else if (header) {
		return headerElem;
	} else {
		return bodyElem;
	}
}

export default function CoreFormLabel({ header, className, children, ...labelProps }: CoreFormLabelProps) {
	return (
		<FormControlLabel
			{...labelProps}
			className={`${styles.formLabel} ${header && children ? styles.stacked : ""} ${className || ""}`}
			label={<CoreLabelTextContent header={header}>{children}</CoreLabelTextContent>}
		/>
	);
}
